import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import Newsletter from "@/components/Newsletter";
import { FadeIn, ChapterLabel } from "@/components/Reveal";
import { TRAILERS, GALLERY, YOUTUBE_URL } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const Lightbox = ({ item, onClose }) => (
  <AnimatePresence>
    {item && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-charcoal/80 p-6 backdrop-blur-sm"
        onClick={onClose}
        data-testid="gallery-lightbox"
      >
        <motion.figure
          initial={{ scale: 0.92, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-h-[85vh] max-w-4xl"
          onClick={(e) => e.stopPropagation()}
        >
          <img src={item.src} alt={item.title} className="max-h-[75vh] w-full rounded-3xl object-contain" />
          <figcaption className="mt-4 text-center font-serif text-xl italic text-cream">{item.title}</figcaption>
          <button onClick={onClose} data-testid="lightbox-close-button" aria-label="Close" className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center rounded-full bg-cream text-charcoal shadow-lg transition-transform hover:scale-110">
            <X size={18} />
          </button>
        </motion.figure>
      </motion.div>
    )}
  </AnimatePresence>
);

export default function Media() {
  useSEO(
    "Read-Aloud Videos & Artwork | The Cap Chronicles",
    "Watch read-aloud videos for kids ages 4-8, browse storybook artwork, and peek behind the scenes of The Cap Chronicles on YouTube."
  );
  const [selected, setSelected] = useState(null);

  return (
    <main>
      <PageHero
        kicker="Media"
        title="Watch the world"
        italic="come alive."
        description="Read-aloud videos for kids ages 4-8, frames from the stories, and quiet moments from the studio floor."
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12" data-testid="trailers-section">
        <ChapterLabel num="01" title="On YouTube" />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TRAILERS.map((t, i) => {
            const inner = (
              <>
                <img src={t.img} alt={t.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/75 via-charcoal/10 to-transparent" />
                <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-cream/90 text-charcoal shadow-xl transition-transform duration-300 group-hover:scale-110">
                  <Play size={18} fill="currentColor" />
                </span>
                <span className="absolute right-4 top-4 rounded-full bg-charcoal/60 px-3 py-1 text-xs font-medium text-cream backdrop-blur-sm">{t.length}</span>
                <h3 className="absolute bottom-0 left-0 p-5 font-serif text-lg leading-snug text-cream">{t.title}</h3>
              </>
            );
            const cls = "group relative block h-72 overflow-hidden rounded-3xl";
            return (
              <FadeIn key={t.title} delay={i * 0.08}>
                {t.to ? (
                  <Link to={t.to} data-testid={`trailer-card-${i}`} className={cls}>{inner}</Link>
                ) : (
                  <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid={`trailer-card-${i}`} className={cls}>{inner}</a>
                )}
              </FadeIn>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32" data-testid="gallery-section">
        <ChapterLabel num="02" title="Artwork & Behind the Scenes" />
        <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 [&>div]:mb-6">
          {GALLERY.map((g, i) => (
            <FadeIn key={g.title} delay={(i % 3) * 0.08} className="break-inside-avoid">
              <button onClick={() => setSelected(g)} data-testid={`gallery-item-${i}`} className="group relative block w-full overflow-hidden rounded-3xl text-left">
                <img src={g.src} alt={g.title} className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 translate-y-2 p-5 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">{g.kind}</p>
                  <p className="font-serif text-lg text-cream">{g.title}</p>
                </div>
              </button>
            </FadeIn>
          ))}
        </div>
      </section>

      <Lightbox item={selected} onClose={() => setSelected(null)} />
      <Newsletter />
    </main>
  );
}
